/* ==========================================================================
   CUSTOM: Promotion popup (email capture -> discount code)
   Loaded by sections/promo-popup.liquid

   Flow
   ----
   1. Popup auto-opens after the configured delay (respecting the "show again
      after N days" rule and the "already subscribed" flag in localStorage).
   2. The form is a real {% form 'customer' %}, so the email lands in the
      Shopify customer / marketing list. We intercept the submit and post it in
      the background so the page never reloads.
   3. On success we swap to the coupon screen (copy code + apply & shop).

   Phone number: Shopify's native customer form has no phone field, so the
   phone input is deliberately NOT posted. When "save phone as tag" is on we
   fold it into contact[tags] as `phone:9198...` on the customer record.
   ========================================================================== */
(function () {
  'use strict';

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  var MOBILE_QUERY = '(max-width: 749px)';

  function storage() {
    try {
      var test = '__ccp__';
      window.localStorage.setItem(test, '1');
      window.localStorage.removeItem(test);
      return window.localStorage;
    } catch (e) {
      return null; // private mode / blocked — popup just shows every time
    }
  }

  var store = storage();

  // Survives theme-editor section re-renders (the element is replaced, this
  // closure is not) so an open popup doesn't restart its delay on every edit.
  var openState = {};

  // sessionStorage marks "we just posted" so a page reload can still land the
  // customer on the coupon screen.
  function readSession(key) {
    try { return window.sessionStorage.getItem(key); } catch (e) { return null; }
  }

  function writeSession(key, value) {
    try { window.sessionStorage.setItem(key, value); } catch (e) { /* noop */ }
  }

  function clearSession(key) {
    try { window.sessionStorage.removeItem(key); } catch (e) { /* noop */ }
  }

  function readFlag(key) {
    return store ? store.getItem(key) : null;
  }

  function writeFlag(key, value) {
    if (store) {
      try { store.setItem(key, value); } catch (e) { /* quota — ignore */ }
    }
  }

  class CcPromoPopup extends HTMLElement {
    connectedCallback() {
      this.root = this.querySelector('[data-ccp-root]');
      if (!this.root) return;

      this.sectionId = this.dataset.sectionId || '';
      // Query the tag itself — don't depend on Shopify's {% form %} having
      // carried our class through, or the submit stops being intercepted.
      this.form = this.querySelector('form');
      this.emailInput = this.querySelector('[data-ccp-email]');
      this.phoneInput = this.querySelector('[data-ccp-phone]');
      this.tagsInput = this.querySelector('[data-ccp-tags]');
      this.submitBtn = this.querySelector('[data-ccp-submit]');
      this.errorBox = this.querySelector('[data-ccp-error]');
      this.formState = this.querySelector('[data-ccp-state="form"]');
      this.successState = this.querySelector('[data-ccp-state="success"]');
      this.copyBtn = this.querySelector('[data-ccp-copy]');

      this.delay = parseInt(this.dataset.delay, 10) || 0;
      this.reappearDays = parseInt(this.dataset.reappearDays, 10);
      if (isNaN(this.reappearDays)) this.reappearDays = 7;
      this.hideAfterSubmit = this.dataset.hideAfterSubmit === 'true';
      this.enableMobile = this.dataset.enableMobile !== 'false';
      this.storePhoneTag = this.dataset.storePhoneTag === 'true';
      this.phoneRequired = this.dataset.phoneRequired === 'true';
      this.baseTags = this.dataset.tags || '';
      this.designMode = !!(window.Shopify && window.Shopify.designMode);

      this.closedKey = 'ccPromoPopup:' + this.sectionId + ':closed';
      this.doneKey = 'ccPromoPopup:' + this.sectionId + ':subscribed';
      this.pendingKey = 'ccPromoPopup:' + this.sectionId + ':pending';

      this.lastFocused = null;
      this.timer = null;

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onSubmit = this.onSubmit.bind(this);

      this.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);
      // Delegated: submit bubbles, so this fires even if the form lookup above
      // ever came back null. Never let the browser post this form natively.
      this.addEventListener('submit', this.onSubmit);

      // Safety net — if a submit did slip through as a real page post (JS error,
      // blocked fetch fallback below), Shopify reloads to ?customer_posted=true.
      // Pick that up and show the coupon instead of an empty form.
      if (!this.designMode && readSession(this.pendingKey) === 'true') {
        clearSession(this.pendingKey);
        if (window.location.search.indexOf('customer_posted=true') !== -1) {
          writeFlag(this.doneKey, 'true');
          this.showSuccess();
          this.open();
          return;
        }
      }

      // Theme editor: honour the delay just like the live site, but ignore the
      // "already closed / already subscribed" rules so it always comes back.
      // It re-opens instantly when the merchant selects the section, or when a
      // setting change re-renders a popup that was already open.
      if (this.designMode) {
        this.bindDesignMode();
        if (this.dataset.previewSuccess === 'true') this.showSuccess();
        if (openState[this.sectionId]) {
          this.open();
        } else {
          this.scheduleOpen();
        }
        return;
      }

      this.scheduleOpen();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      if (this.timer) clearTimeout(this.timer);
      document.body.classList.remove('cc-promo-open');
    }

    /* ---------------- open / close ---------------- */

    scheduleOpen() {
      if (!this.enableMobile && window.matchMedia(MOBILE_QUERY).matches) return;

      // Frequency rules are skipped in the theme editor so the merchant isn't
      // locked out of previewing after one close.
      if (!this.designMode) {
        if (this.hideAfterSubmit && readFlag(this.doneKey) === 'true') return;

        var closedAt = parseInt(readFlag(this.closedKey), 10);
        if (!isNaN(closedAt) && this.reappearDays > 0) {
          var elapsed = Date.now() - closedAt;
          if (elapsed < this.reappearDays * 24 * 60 * 60 * 1000) return;
        }
      }

      if (this.timer) clearTimeout(this.timer);
      this.timer = setTimeout(this.open.bind(this), this.delay * 1000);
    }

    open() {
      if (this.root.classList.contains('is-open')) return;
      this.lastFocused = document.activeElement;
      this.root.classList.add('is-open');
      this.root.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cc-promo-open');
      openState[this.sectionId] = true;

      var focusTarget = this.successState && !this.successState.hidden
        ? this.copyBtn
        : this.emailInput;
      if (focusTarget) {
        try { focusTarget.focus({ preventScroll: true }); } catch (e) { focusTarget.focus(); }
      }
    }

    close(remember) {
      if (this.timer) clearTimeout(this.timer);
      this.root.classList.remove('is-open');
      this.root.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cc-promo-open');
      openState[this.sectionId] = false;

      if (remember !== false && !this.designMode) {
        writeFlag(this.closedKey, String(Date.now()));
      }
      if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
        this.lastFocused.focus();
      }
    }

    onDocumentClick(event) {
      if (event.target.closest('[data-ccp-close]')) {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.target.closest('[data-ccp-copy]')) {
        event.preventDefault();
        this.copyCode();
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.root.classList.contains('is-open')) {
        this.close();
      }
    }

    /* ---------------- submit ---------------- */

    onSubmit(event) {
      event.preventDefault();
      this.hideError();

      var email = (this.emailInput.value || '').trim();
      if (!EMAIL_RE.test(email)) {
        this.emailInput.classList.add('is-invalid');
        this.emailInput.focus();
        this.showError(this.dataset.errorEmail || 'Please enter a valid email address.');
        return;
      }
      this.emailInput.classList.remove('is-invalid');

      var phone = this.phoneInput ? (this.phoneInput.value || '').trim() : '';
      if (this.phoneInput && this.phoneRequired && !phone) {
        this.phoneInput.classList.add('is-invalid');
        this.phoneInput.focus();
        this.showError(this.dataset.errorPhone || 'Please enter your phone number.');
        return;
      }
      if (this.phoneInput) this.phoneInput.classList.remove('is-invalid');

      // Fold the phone into the customer tags (commas are tag separators, so strip them)
      if (this.tagsInput) {
        var tags = this.baseTags;
        if (phone && this.storePhoneTag) {
          var clean = phone.replace(/[^\d+]/g, '');
          if (clean) tags = (tags ? tags + ',' : '') + 'phone:' + clean;
        }
        this.tagsInput.value = tags;
      }

      this.setBusy(true);

      // Marks the post so a reload can still reach the coupon screen.
      writeSession(this.pendingKey, 'true');

      var self = this;
      var form = this.form || (event.target.tagName === 'FORM' ? event.target : null);
      if (!form) {
        this.setBusy(false);
        clearSession(this.pendingKey);
        this.showError(this.dataset.errorGeneric || 'Something went wrong. Please try again.');
        return;
      }

      fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' }
      })
        .then(function (response) {
          // Shopify 302s to ?customer_posted=true on success. Any 2xx/3xx means
          // Shopify accepted the post, so treat it as subscribed — the previous
          // check scanned the whole returned page for a stray `.errors` element
          // and reported a failure on submissions that had actually worked.
          if (!response.ok) {
            throw new Error('HTTP ' + response.status);
          }
          self.setBusy(false);
          clearSession(self.pendingKey);
          writeFlag(self.doneKey, 'true');
          self.showSuccess();
        })
        .catch(function (error) {
          // Shopify can reject background posts (e.g. 430 security rejection).
          // Fall back to a real form post so the customer still subscribes —
          // the pending marker brings them back to the coupon after the reload.
          console.warn('[promo-popup] background subscribe failed, falling back to a normal form post:', error);
          form.submit();
        });
    }

    setBusy(busy) {
      if (!this.submitBtn) return;
      this.submitBtn.setAttribute('aria-busy', busy ? 'true' : 'false');
      this.submitBtn.disabled = !!busy;
    }

    showError(message) {
      if (!this.errorBox) return;
      this.errorBox.textContent = message;
      this.errorBox.hidden = false;
    }

    hideError() {
      if (!this.errorBox) return;
      this.errorBox.hidden = true;
      this.errorBox.textContent = '';
    }

    showSuccess() {
      if (this.formState) this.formState.hidden = true;
      if (this.successState) {
        this.successState.hidden = false;
        if (this.copyBtn) {
          try { this.copyBtn.focus({ preventScroll: true }); } catch (e) { /* noop */ }
        }
      }
    }

    /* ---------------- coupon ---------------- */

    copyCode() {
      var code = this.dataset.code || '';
      if (!code) return;

      var self = this;
      var done = function () {
        var label = self.copyBtn.querySelector('[data-ccp-copy-label]') || self.copyBtn;
        if (!self.copyBtn.dataset.originalLabel) {
          self.copyBtn.dataset.originalLabel = label.textContent;
        }
        label.textContent = self.dataset.copiedLabel || 'Copied!';
        self.copyBtn.classList.add('is-copied');
        setTimeout(function () {
          label.textContent = self.copyBtn.dataset.originalLabel;
          self.copyBtn.classList.remove('is-copied');
        }, 2000);
      };

      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(code).then(done).catch(function () {
          self.legacyCopy(code, done);
        });
      } else {
        this.legacyCopy(code, done);
      }
    }

    legacyCopy(code, done) {
      var helper = document.createElement('textarea');
      helper.value = code;
      helper.setAttribute('readonly', '');
      helper.style.position = 'fixed';
      helper.style.opacity = '0';
      document.body.appendChild(helper);
      helper.select();
      try { document.execCommand('copy'); done(); } catch (e) { /* noop */ }
      document.body.removeChild(helper);
    }

    /* ---------------- theme editor ---------------- */

    bindDesignMode() {
      var self = this;
      document.addEventListener('shopify:section:select', function (event) {
        if (event.detail.sectionId === self.sectionId) self.open();
      });
      document.addEventListener('shopify:section:deselect', function (event) {
        if (event.detail.sectionId === self.sectionId) self.close(false);
      });
    }
  }

  if (!customElements.get('cc-promo-popup')) {
    customElements.define('cc-promo-popup', CcPromoPopup);
  }
})();
